import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO head + JSON-LD blocks.
 * Renders title, description, canonical, OG tags, and any schemas you pass in.
 */
export default function SeoHelmet({ title, description, canonical, image, schema }) {
    const fullTitle = title.endsWith("OpenCSV") ? title : `${title} | OpenCSV`;
    const url = canonical || (typeof window !== "undefined" ? window.location.href : "https://opencsv.io");
    const img = image || "https://opencsv.io/logo-icon.png";
    const schemas = Array.isArray(schema) ? schema : (schema ? [schema] : []);
    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={img} />
            <meta property="og:site_name" content="OpenCSV" />
            <meta property="og:type" content="website" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={img} />
            {schemas.map((s, i) => (
                <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
            ))}
        </Helmet>
    );
}

export const softwareAppSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "OpenCSV",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web, iOS, Android, Windows, macOS, Linux",
    "description": "Lightweight, lightning-fast CSV viewer and editor. Free to view, $9.99/mo Pro for editing — 14-day free trial.",
    "url": "https://opencsv.io",
    "offers": { "@type": "Offer", "price": "1.00", "priceCurrency": "USD" },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "12" },
};

export function faqSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": items.map((i) => ({
            "@type": "Question",
            "name": i.q,
            "acceptedAnswer": { "@type": "Answer", "text": i.a },
        })),
    };
}

export function breadcrumbSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((it, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": it.label,
            "item": it.href.startsWith("http") ? it.href : `https://opencsv.io${it.href}`,
        })),
    };
}
